import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { AuthUserService } from '../../services/auth-user.service';
import { PermissionsService } from '../../services/permissions.service';

/**
 * Factory de guards de autorización por permiso.
 *
 * Espera a que los permisos del usuario estén cargados antes de evaluar — es
 * indispensable, porque `hasPermission` es síncrono pero la app no carga los
 * permisos al arrancar: cada página los pide defensivamente en su `ngOnInit`.
 * En una navegación directa por URL el guard corre antes de eso, así que sin la
 * espera rechazaría a todo el mundo.
 *
 * Debe declararse *después* de `authGuard` en `canActivate`, para que un usuario
 * no autenticado caiga en el flujo de login y no en el de "sin permiso".
 *
 * @param slugOrSlugs Slug del permiso requerido (ej. `view_integrations`), o una
 *   lista de slugs alternativos — basta con tener UNO de ellos (OR). Útil para
 *   una página con varias secciones gateadas por distintos permisos del mismo
 *   módulo, donde cualquiera de ellos debe poder entrar a la ruta.
 * @returns Guard que concede el acceso solo si el usuario tiene el permiso (o,
 *   con una lista, al menos uno de ellos)
 */
export const permissionGuard =
  (slugOrSlugs: string | string[]): CanActivateFn =>
  async () => {
    const authUser = inject(AuthUserService);
    const permissions = inject(PermissionsService);
    const router = inject(Router);

    // Ambos métodos son idempotentes — mismo patrón defensivo que usan las páginas
    await authUser.loadCurrentUser();
    await permissions.loadPermissions();

    const granted = Array.isArray(slugOrSlugs)
      ? slugOrSlugs.some((slug) => permissions.hasPermission(slug))
      : permissions.hasPermission(slugOrSlugs);

    if (granted) {
      return true;
    }

    // Autenticado pero no autorizado: a Inicio, no a login
    await router.navigate(['/home']);
    return false;
  };
